import React, { useState, useMemo, useEffect } from 'react';
import {
  SidebarItems,
  Icon,
  Button,
  TasamaTextLogoWithTagline,
  CompanyLogoUpload,
  type SidebarItemConfig,
  cn,
} from '@tasama/ui';
import { useCompanyLogoUrl } from '../features/my-account/hooks/useMyAccountApi';
import { useTranslation, useDirection } from '@tasama/i18n';
import { useUserContext } from '../stores/user-context.store';
import { useLocation, useNavigate } from 'react-router-dom';
import { PORTAL_ROUTES } from '@tasama/config';

interface AppSidebarProps {
  testID?: string;
  /** Whether the mobile drawer is open */
  isMobileOpen?: boolean;
  /** Callback to close the mobile drawer */
  onMobileClose?: () => void;
}

export const AppSidebar: React.FC<AppSidebarProps> = ({
  testID = 'app-sidebar',
  isMobileOpen = false,
  onMobileClose,
}) => {
  const { t } = useTranslation('portal');
  const dir = useDirection();
  const userContext = useUserContext();
  const { data: logoUrl, isLoading: isLogoLoading } = useCompanyLogoUrl();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Close mobile drawer on route change
  useEffect(() => {
    onMobileClose?.();
  }, [location.pathname, onMobileClose]);

  // Auto-close mobile drawer when resizing past lg breakpoint (900px)
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 900px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) onMobileClose?.();
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [onMobileClose]);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const navigationItems: SidebarItemConfig[] = useMemo(() => {
    const notificationCounts = userContext?.notifications?.modules || {};
    const myTasksDisplay = notificationCounts.myTasks?.displayCount;
    const myTasksBadge =
      myTasksDisplay && myTasksDisplay !== '0' ? myTasksDisplay : undefined;

    return [
      {
        to: '/',
        icon: 'House',
        label: t('navigation.home'),
        exact: true,
      },
      {
        to: '/service-catalogue',
        icon: 'BookOpen',
        label: t('navigation.serviceCatalogue'),
      },
      {
        to: '/dashboards',
        icon: 'LayoutDashboard',
        label: t('navigation.dashboards'),
      },
      {
        to: '/services',
        icon: 'Briefcase',
        label: t('navigation.myServices'),
      },
      {
        to: '/my-tasks',
        icon: 'ClipboardList',
        label: t('navigation.myTasks'),
        ...(myTasksBadge && { badge: myTasksBadge }),
      },
      {
        to: '/support-tickets',
        icon: 'MessageSquareText',
        label: t('navigation.supportTickets'),
        ...(notificationCounts.supportTickets?.unreadTotalCount && {
          badge: String(notificationCounts.supportTickets.unreadTotalCount),
        }),
      },
    ];
  }, [t, userContext?.notifications?.modules]);

  // TDF-1461: TASAMA back-office. Only rendered for a Super Admin, and each item
  // additionally requires its own grant, so a Super Admin without the publish
  // grant sees no section at all rather than an item that would 403.
  const superAdminItems: SidebarItemConfig[] = useMemo(() => {
    if (!userContext?.isSuperAdmin) return [];

    const items: SidebarItemConfig[] = [];
    if (userContext?.canPublishCatalog) {
      items.push({
        to: PORTAL_ROUTES.SUPER_ADMIN_CATALOG_SYNC,
        icon: 'RefreshCcw',
        label: t('navigation.catalogSyncPublish'),
      });
    }
    return items;
  }, [t, userContext?.isSuperAdmin, userContext?.canPublishCatalog]);

  const renderNav = (collapsed: boolean) => (
    <div className="flex flex-col gap-2 w-full min-w-0">
      <SidebarItems items={navigationItems} isCollapsed={collapsed} />
      {superAdminItems.length > 0 && (
        <div
          className="flex flex-col gap-2 mt-4 pt-4 border-t border-border"
          data-testid={`${testID}-super-admin-section`}
        >
          {!collapsed && (
            <span className="px-4 text-xs font-medium tracking-wider text-fg-tertiary">
              {t('navigation.superAdmin')}
            </span>
          )}
          <SidebarItems items={superAdminItems} isCollapsed={collapsed} />
        </div>
      )}
    </div>
  );

  const sidebarContent = renderNav(false);

  return (
    <>
      {/* ── Mobile Drawer ── */}
      {/* Overlay */}
      <div
        className={cn(
          'fixed inset-0 bg-black/40 z-sidepanel lg:hidden transition-opacity duration-300',
          isMobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onMobileClose}
        aria-hidden="true"
      />
      {/* Drawer panel */}
      <nav
        className={cn(
          'fixed top-0 start-0 z-sidepanel h-full w-full',
          'bg-white flex flex-col',
          'transition-transform duration-300 ease-in-out',
          'lg:hidden',
          isMobileOpen
            ? 'translate-x-0'
            : dir === 'rtl'
              ? 'translate-x-full'
              : '-translate-x-full'
        )}
        data-testid={`${testID}-mobile-drawer`}
      >
        {/* Drawer header — matches AppHeader mobile: h-80px, px-4 */}
        <div className="flex items-center justify-between h-[80px] px-4 border-b border-gray-200">
          <TasamaTextLogoWithTagline
            width={166}
            height={35}
            className="w-[166px] h-[35px]"
          />
          <div className="flex items-center gap-3">
            {!isLogoLoading && logoUrl && (
              <CompanyLogoUpload
                logoSrc={logoUrl}
                logoAlt="Company Logo"
                readOnly
                size="sm"
                testID={`${testID}-company-logo`}
              />
            )}
            <button
              type="button"
              onClick={onMobileClose}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-[#f3f4f5] hover:bg-gray-200 cursor-pointer border-none"
              aria-label={t('navigation.closeMenu')}
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
        {/* Drawer nav items */}
        <div className="flex-1 px-4 pt-4 overflow-y-auto">
          {/* TDF-1391: on mobile the Super Admin label and the company field sit
              at the top of the expanded menu, above the navigation. */}
          {userContext?.actingCompany && (
            <div
              className="flex flex-col gap-2 pb-4 mb-2 border-b border-border"
              data-testid={`${testID}-acting-context-mobile`}
            >
              <span className="text-sm font-medium text-fg-primary">
                {t('superAdmin.selectCompany.switchLabel')}
              </span>
              <button
                type="button"
                onClick={() => {
                  onMobileClose?.();
                  navigate(PORTAL_ROUTES.SELECT_COMPANY);
                }}
                aria-label={t('superAdmin.selectCompany.switchAction')}
                className="flex items-center justify-between gap-2 rounded-xl border border-border bg-white px-4 py-2 cursor-pointer w-full"
                data-testid={`${testID}-acting-company-switch-mobile`}
              >
                <span className="text-sm text-fg-primary truncate">
                  {userContext.actingCompany.name}
                </span>
                <Icon
                  name="Pencil"
                  size={16}
                  className="text-fg-brand-primary shrink-0"
                />
              </button>
            </div>
          )}
          {sidebarContent}
        </div>
      </nav>

      {/* ── Desktop Sidebar ── */}
      <aside
        className={cn(
          'hidden lg:flex',
          isCollapsed ? 'w-[88px]' : 'w-[300px]',
          // No explicit height — relies on default flex `align-items:
          // stretch` to fill the cross axis of the row, which now has a
          // definite height from `min-h-full` propagating through the
          // layout chain. Replaces the old fixed 817px which never
          // adapted to the actual viewport.
          'bg-background',
          'flex-col justify-between',
          'p-xl rounded-4xl overflow-hidden',
          'transition-all duration-300 ease-in-out will-change-[width]'
        )}
        data-testid={testID}
      >
        {renderNav(isCollapsed)}

        {/* Collapse toggle */}
        <div
          className={cn(
            'flex w-full shrink-0',
            isCollapsed ? 'justify-center' : 'justify-end'
          )}
        >
          <Button
            variant="quaternary"
            size="md"
            onClick={toggleCollapse}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-[#f3f4f5] hover:bg-gray-200 transition-colors"
          >
            <Icon
              name={isCollapsed ? 'PanelRightClose' : 'PanelRightOpen'}
              className={dir === 'rtl' ? 'icon-rtl-flip' : undefined}
              size={16}
            />
          </Button>
        </div>
      </aside>
    </>
  );
};
